import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { useSelector } from 'react-redux'

import FacebookAdPreviewSkeleton from '../FacebookPreviews/reusablePreviewComponents/FacebookAdPreviewSkeleton'
import CarouselInstagramStoriesPreview from '../FacebookPreviews/CarouselInstagramStoriesPreview'
import FacebookSingleMediaAdPreview from '../FacebookPreviews/FacebookSingleMediaAdPreview'
import FacebookCarouselAdPreview from '../FacebookPreviews/FacebookCarouselAdPreview'
import FacebookExistingPostPreview from '../FacebookPreviews/FacebookExistingPostPreview'
import { AdFacebookPreviewContextProvider } from './AdFacebookPreviewContextProvider'

import useFlatFormikValues from '../../../forms/Facebook/AdForms/useFlatFormikValues'
import { getFirstMediaItem, getMediaItemImageUrl, isStoryPlacements } from './helpers'
import { useGetImageMediaRatio, useIsStoryRatio } from './hooks'

import { adModifiedDataSelector } from '../../../modules/selectors/ads'
import { facebookImageUrlsSelector } from '../../../modules/selectors/mediaFiles'

import {
  CAROUSEL_ITEMS,
  FORMAT_CAROUSEL,
  FORMAT_EXISTING_POST,
  FORMAT_OPTION,
  FORMAT_SINGLE,
  PRIMARY_TEXT_LIST,
  STORIES_ITEM_SELECTED_INDEX,
  STORIES_ITEMS,
  TEXT_BLANK,
  TEXT_ENTER,
  TEXT_OPTION
} from '../../../forms/Facebook/AdForms/fields'

import { MEDIA_FILE_OPTION } from '../../../forms/ReusableFormFields/AdFileUpload/fields'

const AdFacebookPreview = props => {
  const imageUrls = useSelector(facebookImageUrlsSelector)

  const { originalValues, ad, placementPositions, isLoading, isSoundMuted, isEditForm, isAdApprovalsStyle } = props

  const [storiesSelectedItem, setStoriesSelectedItem] = useState(0)

  const modifiedValues = useSelector(adModifiedDataSelector)

  const handleLeftSideClick = useCallback(() => {
    setStoriesSelectedItem(storiesSelectedItem - 1)
  }, [storiesSelectedItem])

  const handleRightSideClick = useCallback(() => {
    setStoriesSelectedItem(storiesSelectedItem + 1)
  }, [storiesSelectedItem])

  const formikValues = {
    ...originalValues,
    ...(isEditForm && { ...modifiedValues }),
    [STORIES_ITEM_SELECTED_INDEX]: storiesSelectedItem,
    [TEXT_OPTION]:
      !!originalValues?.[PRIMARY_TEXT_LIST]?.[0].text || !!modifiedValues[PRIMARY_TEXT_LIST]?.[0].text
        ? TEXT_ENTER
        : TEXT_BLANK
  }

  const flatFormikValues = useFlatFormikValues({ formikValues, allowUseSingleMediaItem: true })

  // todo refactor to use formikValues[SINGLE_MEDIA_ITEM]
  const mediaItem = getFirstMediaItem(formikValues)

  // If media is video, initially we represent it as image
  // to get actual image url we use helper function
  const mediaItemImageUrl = getMediaItemImageUrl(mediaItem, imageUrls)

  const { mediaRatio: mediaThumbnailImageRatio, isMediaError: mediaThumbnailImageError } =
    useGetImageMediaRatio(mediaItemImageUrl)

  const isStoryRatio = useIsStoryRatio(mediaThumbnailImageRatio)

  const showStoryPreview = useMemo(
    () =>
      isStoryPlacements(placementPositions) ||
      // On Ad approvals page we can't check placement positions, so instead we check media ratio
      (isAdApprovalsStyle && isStoryRatio),
    [isStoryRatio, isAdApprovalsStyle, placementPositions]
  )
  const showCarouselPreview = formikValues[FORMAT_OPTION] === FORMAT_CAROUSEL
  const showSingleMediaPreview = formikValues[FORMAT_OPTION] === FORMAT_SINGLE
  const isExistingPost = formikValues[FORMAT_OPTION] === FORMAT_EXISTING_POST

  const mediaThumbnailDidLoading = mediaThumbnailImageRatio || mediaThumbnailImageError

  const showSkeleton =
    isLoading ||
    // Still show skeleton if media ratio is not calculated yet, existingPost doesn't have media
    (!mediaThumbnailDidLoading && !isExistingPost)

  if (showSkeleton) {
    return <FacebookAdPreviewSkeleton />
  }

  if (showStoryPreview) {
    return (
      <AdFacebookPreviewContextProvider ad={ad}>
        <CarouselInstagramStoriesPreview
          viewAdOnFacebook={ad?.preview}
          formikValues={flatFormikValues}
          elementInProgress={STORIES_ITEMS}
          handleLeftSideClick={handleLeftSideClick}
          handleRightSideClick={handleRightSideClick}
          mediaThumbnailImageRatio={mediaThumbnailImageRatio}
        />
      </AdFacebookPreviewContextProvider>
    )
  }

  if (showCarouselPreview) {
    return (
      <AdFacebookPreviewContextProvider ad={ad}>
        <FacebookCarouselAdPreview
          viewAdOnFacebook={ad?.preview}
          formikValues={flatFormikValues}
          elementInProgress={CAROUSEL_ITEMS}
          isSoundMuted={isSoundMuted}
        />
      </AdFacebookPreviewContextProvider>
    )
  }

  if (showSingleMediaPreview) {
    return (
      <AdFacebookPreviewContextProvider ad={ad}>
        <FacebookSingleMediaAdPreview
          viewAdOnFacebook={ad?.preview}
          formikValues={flatFormikValues}
          elementInProgress={MEDIA_FILE_OPTION}
          isSoundMuted={isSoundMuted}
          isInProgress={true}
          mediaThumbnailImageRatio={mediaThumbnailImageRatio}
        />
      </AdFacebookPreviewContextProvider>
    )
  }

  if (isExistingPost) {
    return (
      <FacebookExistingPostPreview
        // We can't represent existing post thumbnail because it's missing in payload
        values={flatFormikValues}
        elementInProgress={MEDIA_FILE_OPTION}
        isSoundMuted={isSoundMuted}
      />
    )
  }

  return null
}

AdFacebookPreview.propTypes = {
  originalValues: PropTypes.object,
  ad: PropTypes.object,
  placementPositions: PropTypes.array,
  isLoading: PropTypes.bool,
  isSoundMuted: PropTypes.bool,
  isEditForm: PropTypes.bool,
  isAdApprovalsStyle: PropTypes.bool
}

export default AdFacebookPreview
